import AdSenseBanner from "@/components/AdSenseBanner";
import AdSenseServerCard from "@/components/AdSenseServerCard";
import { Fragment, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SectionHeader from "@/components/SectionHeader";
import { fetchShopProducts, fetchShopCategories, fileUrl } from "@/lib/api";
import { useShop } from "@/context/ShopContext";
import { Coins, ShoppingCart, Sparkles, Search, ArrowRight, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { sfx } from "@/lib/sounds";

export default function Store() {
  const [products, setProducts] = useState(null);
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");
  const { add, count } = useShop();

  useEffect(() => {
    fetchShopProducts().then(setProducts).catch(() => setProducts([]));
    fetchShopCategories().then(setCats).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    let list = products || [];
    if (cat !== "All") list = list.filter((p) => p.category === cat);
    if (q.trim()) list = list.filter((p) => p.name.toLowerCase().includes(q.trim().toLowerCase()));
    return list;
  }, [products, cat, q]);

  const categories = ["All", ...cats];

  const onAdd = (p) => {
    add(p, 1);
    sfx.click();
    toast.success(`${p.name} added to cart`);
  };

  return (
    <div className="pt-32 pb-24 px-4 sm:px-6" data-testid="page-store">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
          <SectionHeader
            eyebrow="LostDC Store"
            title="Gear Up The Network"
            subtitle="Ranks, keys, cosmetics & battle passes — pay with real money or your DC Coins."
          />
          <Link
            to="/cart"
            data-testid="store-cart-link"
            className="mt-2 inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#FFBF00] text-black font-display font-bold shadow-lg shadow-[#FFBF00]/20 hover:scale-105 transition-all duration-200"
          >
            <ShoppingCart size={18} /> Cart{count ? ` (${count})` : ""}
          </Link>
        </div>

        {/* Categories & Search */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10 pb-6 border-b border-white/10">
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                data-testid={`store-cat-${c.toLowerCase().replace(/\s/g, "-")}`}
                className={`px-5 py-2 rounded-full text-xs font-semibold uppercase tracking-wider transition-all duration-200 ${
                  cat === c
                    ? "bg-[#FFBF00] text-black shadow-md shadow-[#FFBF00]/20"
                    : "glass hover:border-[#FFBF00]/50 text-white/80 hover:text-white"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="relative w-full sm:w-64">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              data-testid="store-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search store…"
              className="w-full pl-10 pr-4 py-2.5 rounded-full glass text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[#FFBF00]/50 transition-all"
            />
          </div>
        </div>

        {/* Product Cards Grid */}
        {products === null ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-[750px] rounded-2xl glass skeleton animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div
            className="text-center py-24 text-white/50 glass rounded-2xl border border-white/10"
            data-testid="store-empty"
          >
            <p className="text-lg font-medium">
              No products found matching your criteria.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((p, index) => {
              const imageSrc = p.images?.[0] ? fileUrl(p.images[0]) : null;

              // First ad after product 4, then every 6 products.
              const showAdsAfterProduct =
                index >= 3 && (index - 3) % 6 === 0;

              return (
                <Fragment key={p.slug}>
                  <div
                    data-testid={`store-item-${p.slug}`}
                    className={`group relative rounded-2xl glass overflow-hidden flex flex-col justify-between border border-white/10 hover:border-[#FFBF00]/40 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-[#FFBF00]/5 ${
                      p.featured ? "ring-2 ring-[#FFBF00]/50" : ""
                    }`}
                  >
                    {p.featured && (
                      <div className="absolute top-3 right-3 z-10 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FFBF00] text-black text-[10px] font-extrabold uppercase tracking-widest shadow-md">
                        <Sparkles size={12} /> Featured
                      </div>
                    )}

                    <div className="p-6 flex flex-col flex-1">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-[#FF8C00] mb-1.5">
                        {p.category}
                      </div>

                      <Link
                        to={`/store/${p.slug}`}
                        className="font-display text-xl font-bold leading-snug text-white hover:text-[#FFBF00] transition-colors pr-16"
                      >
                        {p.name}
                      </Link>

                      <ul className="mt-4 space-y-1.5 text-xs text-white/70 flex-1">
                        {(p.perks || []).slice(0, 4).map((perk, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <span className="text-[#FFBF00]">•</span>
                            <span className="line-clamp-1">{perk}</span>
                          </li>
                        ))}
                      </ul>

                      <div className="mt-6 pt-4 pb-2 border-t border-white/10 flex items-center justify-between gap-2">
                        <div>
                          <div className="font-display text-2xl font-black text-white">
                            ₹{p.price_inr}
                          </div>

                          {p.dc_price != null && (
                            <div className="inline-flex items-center gap-1 text-[#FFBF00] text-xs font-bold mt-0.5">
                              <Coins size={13} /> {p.dc_price} DC
                            </div>
                          )}
                        </div>

                        <button
                          onClick={() => onAdd(p)}
                          data-testid={`store-add-${p.slug}`}
                          className="px-5 py-2.5 rounded-xl bg-[#FFBF00] text-black text-xs font-bold uppercase tracking-wider hover:bg-[#ffca28] active:scale-95 transition-all shadow-md shadow-[#FFBF00]/10 flex items-center gap-1.5"
                        >
                          Add <ArrowRight size={14} />
                        </button>
                      </div>
                    </div>

                    <Link
                      to={`/store/${p.slug}`}
                      className="relative block aspect-[9/16] h-[380px] sm:h-[420px] md:h-[460px] w-full overflow-hidden bg-black/50 border-t border-white/10"
                    >
                      {imageSrc ? (
                        <img
                          src={imageSrc}
                          alt={p.name}
                          loading="lazy"
                          className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500 ease-out"
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-white/20 bg-gradient-to-b from-white/5 to-transparent">
                          <ImageOff size={36} />
                          <span className="text-xs mt-2">No Preview Available</span>
                        </div>
                      )}

                      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-transparent opacity-60 group-hover:opacity-30 transition-opacity" />
                    </Link>
                  </div>

                  {showAdsAfterProduct && (
                    <div
                      key={`store-ad-${index}`}
                      className="rounded-2xl glass border border-white/10 p-3 flex flex-col min-w-0"
                      data-testid={`store-ad-${index + 1}`}
                    >
                      <div className="text-[9px] uppercase tracking-widest text-white/40 mb-2">
                        Advertisement
                      </div>

                      <div className="min-h-[250px] flex-1 flex items-center justify-center overflow-hidden">
                        <AdSenseServerCard />
                      </div>
                    </div>
                  )}
                </Fragment>
              );
            })}
          </div>
        )}

        {/* Bottom AdSense Banner */}
        <div className="mt-12">
          <AdSenseBanner />
        </div>
      </div>
    </div>
  );
}